import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { venuesApi, leaguesApi } from '../services/api';

function Venues({ user, onLogout }) {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    logoUrl: '',
    kioskMessage: '',
    kioskColor: '#0a1628',
    phone: '',
    websiteUrl: '',
    facebookUrl: '',
    hoursText: '',
    description: '',
    rating: '',
    ratingCount: '',
  });
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [isAddressValidated, setIsAddressValidated] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [validatedAddress, setValidatedAddress] = useState(null);

  const leagueIdRef = useRef(user?.league?.id);

  useEffect(() => {
    const init = async () => {
      if (!leagueIdRef.current) {
        try {
          const res = await leaguesApi.getAll();
          const leagues = res.data?.leagues || res.leagues || [];
          if (leagues.length > 0) leagueIdRef.current = leagues[0].id;
        } catch (err) {
          console.warn('Could not fetch league:', err);
        }
      }
      fetchVenues();
    };
    init();
  }, []);

  const fetchVenues = async () => {
    try {
      setLoading(true);
      const response = await venuesApi.getAll();
      // Handle nested response: { data: { venues: [...] } } or { venues: [...] }
      const venuesList = response.data?.venues || response.venues || response.data || [];
      setVenues(Array.isArray(venuesList) ? venuesList : []);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (venue = null) => {
    if (venue) {
      setEditingVenue(venue);
      setFormData({
        name: venue.name || '',
        address: venue.address || '',
        latitude: venue.latitude || '',
        longitude: venue.longitude || '',
        logoUrl: venue.logoUrl || '',
        kioskMessage: venue.kioskMessage || '',
        kioskColor: venue.kioskColor || '#0a1628',
        phone: venue.phone || '',
        websiteUrl: venue.websiteUrl || '',
        facebookUrl: venue.facebookUrl || '',
        hoursText: venue.hoursText || '',
        description: venue.description || '',
        rating: venue.rating || '',
        ratingCount: venue.ratingCount || '',
      });
      // If venue already has coordinates, consider it validated
      setIsAddressValidated(venue.latitude && venue.longitude);
      setValidatedAddress(venue.address || null);
    } else {
      setEditingVenue(null);
      setFormData({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        logoUrl: '',
        kioskMessage: '',
        kioskColor: '#0a1628',
      });
      setIsAddressValidated(false);
      setValidatedAddress(null);
    }
    setValidationError(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVenue(null);
    setFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      logoUrl: '',
      kioskMessage: '',
      kioskColor: '#0a1628',
    });
    setIsAddressValidated(false);
    setValidationError(null);
    setValidatedAddress(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // If address changes, require re-validation
    if (name === 'address') {
      setIsAddressValidated(false);
      setValidationError(null);
      setValidatedAddress(null);
      // Clear coordinates when address changes
      setFormData(prev => ({
        ...prev,
        [name]: value,
        latitude: '',
        longitude: '',
      }));
    }

    // If lat/long changes manually, require re-validation
    if (name === 'latitude' || name === 'longitude') {
      setIsAddressValidated(false);
      setValidationError(null);
    }
  };

  const handleValidateAddress = async () => {
    if (!formData.address.trim()) {
      setValidationError('Please enter an address to validate');
      return;
    }

    setValidating(true);
    setValidationError(null);

    try {
      const result = await venuesApi.geocodeAddress(formData.address);

      setFormData(prev => ({
        ...prev,
        latitude: result.latitude.toFixed(8),
        longitude: result.longitude.toFixed(8),
      }));

      setIsAddressValidated(true);
      setValidatedAddress(result.displayName);
      setValidationError(null);
    } catch (err) {
      console.error('Error validating address:', err);
      setValidationError(err.message || 'Address not found. Please check and try again.');
      setIsAddressValidated(false);
      setFormData(prev => ({
        ...prev,
        latitude: '',
        longitude: '',
      }));
    } finally {
      setValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate that we have coordinates
    if (!formData.latitude || !formData.longitude) {
      setValidationError('Please validate the address to get coordinates before saving.');
      return;
    }

    // Check if address was validated
    if (!isAddressValidated) {
      setValidationError('Please validate the address before saving.');
      return;
    }

    setSaving(true);

    try {
      const venueData = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        leagueId: user?.league?.id || leagueIdRef.current,
      };

      if (editingVenue) {
        await venuesApi.update(editingVenue.id, venueData);
      } else {
        await venuesApi.create(venueData);
      }

      handleCloseModal();
      fetchVenues();
    } catch (err) {
      console.error('Error saving venue:', err);
      setError('Failed to save venue');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (venueId) => {
    if (!window.confirm('Are you sure you want to delete this venue? This will also affect any games at this venue.')) {
      return;
    }

    try {
      await venuesApi.delete(venueId);
      fetchVenues();
    } catch (err) {
      console.error('Error deleting venue:', err);
      setError('Failed to delete venue');
    }
  };

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />

      <main className="main-content">
        <div className="page-header">
          <h2>Venues</h2>
          <p>Manage poker game venues for your league</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <div className="card-header">
            <h3>All Venues</h3>
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              + Add Venue
            </button>
          </div>

          {loading ? (
            <div className="loading">
              <div className="loading-spinner"></div>
            </div>
          ) : venues.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <h3>No venues yet</h3>
              <p>Add your first venue to start creating games.</p>
              <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                Add Venue
              </button>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Address</th>
                  <th>Coordinates</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {venues.map(venue => (
                  <tr key={venue.id}>
                    <td><strong>{venue.name}</strong></td>
                    <td>{venue.address || '-'}</td>
                    <td>
                      {venue.latitude && venue.longitude ? (
                        <span className="badge badge-success">
                          {parseFloat(venue.latitude).toFixed(4)}, {parseFloat(venue.longitude).toFixed(4)}
                        </span>
                      ) : (
                        <span className="badge badge-warning">Not set</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => navigate('/games', { state: { createGameForVenue: venue } })}
                          title="Create a game at this venue"
                        >
                          + Game
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleOpenModal(venue)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(venue.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal modal-venue" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingVenue ? 'Edit Venue' : 'Add Venue'}</h3>
                <button className="modal-close" onClick={handleCloseModal}>&times;</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="name">Venue Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Carousel Bar"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">Address *</label>
                    <div className="address-input-group">
                      <input
                        type="text"
                        id="address"
                        name="address"
                        className="form-input"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="e.g., 655 N Pearl St., Denver, CO 80203"
                        required
                      />
                      <button
                        type="button"
                        className={`btn ${isAddressValidated ? 'btn-success' : 'btn-secondary'} btn-validate`}
                        onClick={handleValidateAddress}
                        disabled={validating || !formData.address.trim()}
                      >
                        {validating ? 'Validating...' : isAddressValidated ? 'Validated' : 'Validate Address'}
                      </button>
                    </div>
                    {validationError && (
                      <div className="validation-error">{validationError}</div>
                    )}
                    {isAddressValidated && validatedAddress && (
                      <div className="validation-success">
                        <span className="validation-icon">✓</span>
                        Found: {validatedAddress}
                      </div>
                    )}
                  </div>

                  <div className="coordinates-row">
                    <div className="form-group">
                      <label htmlFor="latitude">Latitude</label>
                      <input
                        type="number"
                        id="latitude"
                        name="latitude"
                        className="form-input"
                        value={formData.latitude}
                        onChange={handleInputChange}
                        placeholder="e.g., 39.72854730"
                        step="any"
                        readOnly
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="longitude">Longitude</label>
                      <input
                        type="number"
                        id="longitude"
                        name="longitude"
                        className="form-input"
                        value={formData.longitude}
                        onChange={handleInputChange}
                        placeholder="e.g., -104.97923540"
                        step="any"
                        readOnly
                      />
                    </div>
                  </div>

                  {!isAddressValidated && (formData.latitude || formData.longitude) && (
                    <div className="validation-warning">
                      Coordinates must be validated before saving.
                    </div>
                  )}

                  <h4 style={{ marginTop: '20px', marginBottom: '12px', color: '#333' }}>Kiosk Customization</h4>

                  <div className="form-group">
                    <label>Logo URL</label>
                    <input type="url" name="logoUrl" className="form-input" value={formData.logoUrl} onChange={handleInputChange} placeholder="https://example.com/logo.png" />
                  </div>

                  <div className="form-group">
                    <label>Kiosk Welcome Message</label>
                    <input type="text" name="kioskMessage" className="form-input" value={formData.kioskMessage} onChange={handleInputChange} placeholder="e.g. Welcome to Pitchers Poker Night!" />
                  </div>

                  <div className="form-group">
                    <label>Kiosk Background Color</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="color" name="kioskColor" value={formData.kioskColor} onChange={handleInputChange} style={{ width: 50, height: 36, border: 'none', cursor: 'pointer' }} />
                      <input type="text" name="kioskColor" className="form-input" value={formData.kioskColor} onChange={handleInputChange} placeholder="#0a1628" style={{ flex: 1 }} />
                    </div>
                  </div>

                  <hr style={{ margin: '16px 0', border: 0, borderTop: '1px solid #eee' }} />
                  <div style={{ fontWeight: 700, marginBottom: 12 }}>Public Listing Info</div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" name="phone" className="form-input" value={formData.phone} onChange={handleInputChange} placeholder="(303) 555-0100" />
                  </div>
                  <div className="form-group">
                    <label>Hours</label>
                    <input type="text" name="hoursText" className="form-input" value={formData.hoursText} onChange={handleInputChange} placeholder="Mon–Sun 11am–2am" />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea name="description" className="form-input" value={formData.description} onChange={handleInputChange} rows="3" placeholder="Sports bar with great wings..." />
                  </div>
                  <div className="form-group">
                    <label>Website URL</label>
                    <input type="url" name="websiteUrl" className="form-input" value={formData.websiteUrl} onChange={handleInputChange} placeholder="https://..." />
                  </div>
                  <div className="form-group">
                    <label>Facebook URL</label>
                    <input type="url" name="facebookUrl" className="form-input" value={formData.facebookUrl} onChange={handleInputChange} placeholder="https://facebook.com/..." />
                  </div>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Rating (0-5)</label>
                      <input type="number" name="rating" className="form-input" value={formData.rating} onChange={handleInputChange} min="0" max="5" step="0.1" />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label># of Reviews</label>
                      <input type="number" name="ratingCount" className="form-input" value={formData.ratingCount} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saving || !isAddressValidated}
                    title={!isAddressValidated ? 'Please validate the address first' : ''}
                  >
                    {saving ? 'Saving...' : (editingVenue ? 'Update Venue' : 'Create Venue')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <style>{`
        .modal-venue {
          max-width: 600px;
        }

        .address-input-group {
          display: flex;
          gap: 12px;
        }

        .address-input-group .form-input {
          flex: 1;
        }

        .btn-validate {
          white-space: nowrap;
          min-width: 140px;
        }

        .coordinates-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .coordinates-row .form-input {
          background-color: #f5f5f5;
          cursor: not-allowed;
        }

        .validation-error {
          color: #e74c3c;
          font-size: 13px;
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .validation-success {
          color: #27ae60;
          font-size: 13px;
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 6px;
          background: rgba(39, 174, 96, 0.1);
          padding: 8px 12px;
          border-radius: 6px;
        }

        .validation-icon {
          font-weight: bold;
        }

        .validation-warning {
          color: #f39c12;
          font-size: 13px;
          margin-top: 8px;
          padding: 8px 12px;
          background: rgba(243, 156, 18, 0.1);
          border-radius: 6px;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .address-input-group {
            flex-direction: column;
          }

          .btn-validate {
            min-width: auto;
            width: 100%;
            min-height: 44px;
          }

          .coordinates-row {
            grid-template-columns: 1fr;
          }

          .modal-venue {
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default Venues;
